import { action, computed, observable, makeObservable } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { ChannelStore } from 'modules/channel/stores';
import { notificationError, isSome, getLoginRoute } from 'core/utils';
import { walmartHandoff } from 'common/utils';
import { channelType } from 'common/constants';

class WalmartConnectViewStore extends BaseViewStore {
	@observable walmartChannels = [];
	@observable selectedChannelId = null;
	@observable hasLoaded = false;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.channelStore = new ChannelStore(rootStore);
		this.initialize();
	}

	@computed
	get isAuthenticated() {
		return this.rootStore.authStore.isAuthenticated;
	}

	@computed
	get hasWalmartChannels() {
		return this.walmartChannels.length > 0;
	}

	@action.bound
	async initialize() {
		if (!this.isAuthenticated) {
			// The handoff is already in sessionStorage, so it survives the login round trip and the
			// full page reload that comes with it.
			this.rootStore.routerStore.goTo(getLoginRoute());
			return;
		}

		this.suspend();
		try {
			await this.channelStore.findChannel({
				embed: ['channelType'],
				pageNumber: 1,
				pageSize: 1000,
			});
			this.setWalmartChannels(this.channelStore.response);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.setHasLoaded();
		this.resume();
	}

	@action.bound
	setWalmartChannels(response) {
		const items = isSome(response) && isSome(response.item) ? response.item : [];
		this.walmartChannels = items.filter(
			channel => isSome(channel.channelType) && channel.channelType.name === channelType.walmart
		);
		this.selectedChannelId = this.walmartChannels.length > 0 ? this.walmartChannels[0].id : null;
	}

	@action.bound
	setHasLoaded() {
		this.hasLoaded = true;
	}

	@action.bound
	selectChannel(channelId) {
		this.selectedChannelId = channelId;
	}

	@action.bound
	async onConnect() {
		if (!isSome(this.selectedChannelId)) {
			return;
		}

		this.suspend();
		try {
			const handoff = walmartHandoff.read();
			const result = await this.channelStore.authorizeWalmart({
				channelId: this.selectedChannelId,
				clientType: isSome(handoff) ? handoff.clientType : null,
				authorizeUri: isSome(handoff) ? handoff.walmartCallbackUri : null,
			});

			if (isSome(result) && isSome(result.authorizeUrl)) {
				// Full page navigation: Walmart's consent flow needs the top level browsing context
				// and the callback redirect has to land somewhere the application picks up.
				window.location.assign(result.authorizeUrl);
				return;
			}
			this.rootStore.notificationStore.error(['OAUTH.WALMART.AUTHORIZEFAILED']);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}
}

export default WalmartConnectViewStore;
